import { Fragment } from "react";
import { motion } from "framer-motion";
import { GraduationCap, Gauge, FlaskConical, FolderOpen, Factory, Flag } from "lucide-react";
import { Chapter, MaskedLines, Fade, EASE } from "./motion";

const NODES = [
    { icon: GraduationCap, label: "Engineering Student", sub: "Input — Year 00" },
    { icon: Gauge, label: "Capability", sub: "Skills + Communication" },
    { icon: FlaskConical, label: "Experience", sub: "Internships + Projects" },
    { icon: FolderOpen, label: "Portfolio", sub: "Proof of Work" },
    { icon: Factory, label: "Industry Exposure", sub: "Live Pipelines" },
    { icon: Flag, label: "Career", sub: "Output — Year 04+" },
];

export default function Intro() {
    return (
        <section id="about" className="bg-paper py-20 md:py-40" data-testid="about-section">
            <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-16">
                <Chapter index="CH.02" title="The System" />

                <div className="mt-16 grid gap-14 md:mt-24 lg:grid-cols-12 lg:gap-10">
                    <div className="lg:col-span-7">
                        <MaskedLines
                            stagger={0.13}
                            lineClassName="font-display text-[clamp(2.2rem,7.5vw,5.4rem)] font-semibold leading-[1.12] tracking-[-0.02em] text-ink"
                            lines={[
                                <span key="a">Not a course.</span>,
                                <span key="b" className="text-slate">Not a workshop.</span>,
                                <span key="c" className="text-cobalt">A system.</span>,
                            ]}
                        />
                    </div>
                    <div className="flex flex-col justify-end lg:col-span-5">
                        <Fade delay={0.35}>
                            <p className="text-base leading-relaxed text-slate md:text-lg">
                                QRUS is an AI-enabled talent engineering system, installed inside
                                engineering campuses and run across all four years of a degree.
                                Capability compounds into experience, experience into portfolio,
                                portfolio into career.
                            </p>
                            <p className="mt-6 text-sm leading-relaxed text-slate">
                                Not an add-on. An operating layer.
                            </p>
                        </Fade>
                    </div>
                </div>

                <Fade delay={0.2} className="mt-20 md:mt-28">
                    <div className="relative border border-ink/10 bg-white px-6 py-12 md:px-12 md:py-16">
                        <span className="absolute -left-px -top-px h-3 w-3 border-l-2 border-t-2 border-cobalt" />
                        <span className="absolute -bottom-px -right-px h-3 w-3 border-b-2 border-r-2 border-cobalt" />
                        <div className="mb-12 flex items-center justify-between">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-slate">
                                FIG.03 — QRUS Value Chain
                            </span>
                            <span className="text-[10px] uppercase tracking-[0.3em] text-cobalt">
                                Continuous / 4 Years
                            </span>
                        </div>

                        <div className="flex flex-col items-stretch gap-0 lg:flex-row lg:items-center">
                            {NODES.map((node, i) => (
                                <Fragment key={node.label}>
                                    <Fade delay={0.15 + i * 0.1} y={18} className="flex flex-col items-center text-center lg:flex-1">
                                        <div className="relative grid h-16 w-16 place-items-center border border-cobalt/50 bg-paper">
                                            <span className="absolute -top-2 left-2 bg-white px-1 font-display text-[10px] font-semibold tracking-widest text-cobalt">
                                                0{i + 1}
                                            </span>
                                            <node.icon className="h-6 w-6 text-cobalt" strokeWidth={1.5} />
                                        </div>
                                        <span className="mt-4 text-xs font-semibold uppercase tracking-[0.14em] text-ink md:text-[13px]">
                                            {node.label}
                                        </span>
                                        <span className="mt-1 text-[10px] uppercase tracking-[0.18em] text-slate">
                                            {node.sub}
                                        </span>
                                    </Fade>
                                    {i < NODES.length - 1 && (
                                        <>
                                            <motion.span
                                                className="mx-auto my-4 block h-10 w-px origin-top bg-cobalt/50 lg:hidden"
                                                initial={{ scaleY: 0 }}
                                                whileInView={{ scaleY: 1 }}
                                                viewport={{ once: true }}
                                                transition={{ duration: 0.6, ease: EASE, delay: 0.2 + i * 0.1 }}
                                            />
                                            <motion.span
                                                className="mx-2 hidden h-px w-14 origin-left bg-cobalt/50 lg:block"
                                                initial={{ scaleX: 0 }}
                                                whileInView={{ scaleX: 1 }}
                                                viewport={{ once: true }}
                                                transition={{ duration: 0.6, ease: EASE, delay: 0.2 + i * 0.1 }}
                                            />
                                        </>
                                    )}
                                </Fragment>
                            ))}
                        </div>
                    </div>
                </Fade>
            </div>
        </section>
    );
}
